import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Product, formatPrice } from '@/data/products';
import { useSoldProducts } from '@/hooks/useSoldProducts';

const typewriter = { fontFamily: "'Special Elite', cursive" } as const;

const ProductCard = ({ product, large = false }: { product: Product; large?: boolean }) => {

  const [hovered, setHovered] = useState(false);
  const [pressed, setPressed] = useState(false);
  const { isSold } = useSoldProducts();
  const sold = isSold(product.slug);

  useEffect(() => {
    product.images.forEach((img) => {
      const preload = new window.Image();
      preload.src = img.src;
    });
  }, [product]);

  const hasSecond = product.images.length > 1;
  const activeIndex = hovered && hasSecond ? 1 : 0;

  return (
    <Link to={`/product/${product.slug}`} className="group flex w-full flex-col items-center justify-center">
      <div
        className={
          large
            ? 'h-[72svh] md:h-[78vh] w-full max-w-3xl bg-white relative overflow-hidden'
            : 'aspect-[3/4] bg-white relative overflow-hidden w-full'
        }

        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onTouchStart={() => setPressed(true)}
        onTouchEnd={() => setPressed(false)}
        onTouchCancel={() => setPressed(false)}
      >
        {product.images.map((item, index) => (
          <img
            key={index}
            src={item.src}
            alt={item.alt}
            draggable={false}
            className="absolute inset-[6px] w-[calc(100%-12px)] h-[calc(100%-12px)] object-contain"
            style={{
              opacity: index === activeIndex ? 1 : 0,
              transition: 'opacity 200ms ease-out',
              transform: `scale(${item.scale ?? Math.min(product.scale ?? 1, 1)})`,
            }}
          />
        ))}
        {/* Touch outline overlay – always fully visible above images */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none border border-black transition-opacity duration-150"
          style={{ opacity: pressed ? 1 : 0 }}
        />

        {sold && (
          <span
            className="absolute top-2 left-2 border border-black px-2 py-1 text-[10px] md:text-xs uppercase tracking-wide"
            style={{ ...typewriter, color: '#000000', backgroundColor: '#ffffff' }}
          >
            Sold Out
          </span>
        )}
      </div>
      <div className={large ? 'mt-3 text-center' : 'mt-2 text-center'}>
        <p className={`text-black ${large ? 'text-base md:text-lg' : 'text-sm'}`} style={typewriter}>{product.name}</p>
        {sold ? (
          <p className={`text-black ${large ? 'text-base md:text-lg' : 'text-sm'}`} style={typewriter}>Sold Out</p>
        ) : product.department === 'coming' ? (
          <p className={`text-black ${large ? 'text-base md:text-lg' : 'text-sm'}`} style={typewriter}>Out of stock</p>
        ) : (
          <p className={`text-black ${large ? 'text-base md:text-lg' : 'text-sm'}`} style={typewriter}>{formatPrice(product.priceSek)}</p>
        )}
      </div>

    </Link>
  );
};

export default ProductCard;
